import React, {useState, useRef} from 'react';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import {Tooltip} from 'primereact/tooltip';
import {Calendar} from 'primereact/calendar';
import {InputText} from 'primereact/inputtext';
import {Toast} from 'primereact/toast';
import axios from 'axios';
import {TieredMenu} from 'primereact/tieredmenu';

import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import StorageHelper from '../../../utils/storageHelper';

const AgentReportsTable = () => {
    const [payments, setPayments] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState(() => new Date());
    const [toDate, setToDate] = useState(() => new Date());
    const [agentId, setAgentId] = useState('');

    const dt = useRef(null);
    const menu = useRef(null);
    const toast = useRef(null);

    const fetchAgentReports = async () => {
        const effectiveFromDate = fromDate ?? new Date();
        const effectiveToDate = toDate ?? new Date();

        setLoading(true);

        try {
            const formattedFromDate = effectiveFromDate.toISOString().split('T')[0];
            const formattedToDate = effectiveToDate.toISOString().split('T')[0];

            const params = {
                from: formattedFromDate,
                to: formattedToDate
            };

            if (agentId?.trim()) {
                params.agentId = agentId.trim();
            }

            const response = await axios.get(
                `${process.env.REACT_APP_DOVESAPI}report/agents`,
                {
                    params,
                    headers: {
                        Authorization: 'Bearer ' + StorageHelper.get('token')
                    }
                }
            );

            if (response.data.status === "SUCCESS") {
                setPayments(response.data.responseBody);
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Agent reports loaded successfully',
                    life: 3000
                });
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch agent reports',
                    life: 3000
                });
            }
        } catch (error) {
            console.log(error);
            let message = 'Failed to fetch agent reports. Please try again.';

            if (error.response) {
                if (error.response.status === 404) {
                    message = error.response.data?.detail || 'Agent not found.';
                } else if (error.response.status === 400) {
                    message = error.response.data?.detail || 'Invalid request.';
                } else if (error.response.status === 500) {
                    message = 'Server error. Please contact support.';
                }
            } else if (error.request) {
                message = 'No response from server. Check your connection.';
            }

            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: message,
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const exportCSV = (selectionOnly) => {
        dt.current.exportCSV({selectionOnly});
    };

    const onGlobalFilter = (event) => {
        setGlobalFilter(event.target.value);
    };

    const amountBodyTemplate = (rowData) => {
        return `${parseFloat(rowData.amount || 0).toFixed(2)}`;
    };

    const dateBodyTemplate = (rowData, field) => {
        const date = rowData[field];
        if (!date) return '-';
        return new Date(date).toLocaleString();
    };

    const getStatusStyle = (status) => {
        if (!status) return {color: '#A0A0A0', backgroundColor: '#F5F5F5'};

        const upperStatus = status.toUpperCase();

        switch (upperStatus) {
            case 'PENDING':
                return {color: '#FCB700', backgroundColor: '#FFF8E1'};
            case 'SUCCESS':
            case 'SUCCESSFUL':
            case 'COMPLETED':
                return {color: '#00D390', backgroundColor: '#E6FFF7'};
            case 'FAILED':
            case 'DECLINED':
                return {color: '#FF6266', backgroundColor: '#FFECEC'};
            case 'PROCESSING':
                return {color: '#00BAFE', backgroundColor: '#E6F7FF'};
            default:
                if (upperStatus.includes('NMB') || upperStatus.includes('CR')) {
                    return {color: '#00D390', backgroundColor: '#E6FFF7'};
                }
                return {color: '#A0A0A0', backgroundColor: '#F5F5F5'};
        }
    };

    const paymentStatusBodyTemplate = (rowData) => (
        <span
            style={{
                ...getStatusStyle(rowData.paymentStatus),
                padding: '0.25rem 0.75rem',
                borderRadius: '0.375rem',
                fontWeight: '500',
                fontSize: '0.875rem'
            }}
        >
            {rowData.paymentStatus || 'N/A'}
        </span>
    );

    const globalFilterElement = (
        <InputText
            placeholder="Search payments..."
            value={globalFilter}
            onChange={onGlobalFilter}
            style={{width: '100%'}}
        />
    );

    const items = [
        {
            label: 'Download CSV',
            icon: 'pi pi-file-excel',
            command: () => exportCSV(false)
        }
    ];

    return (
        <div className="card home-chart">
            <Toast ref={toast}/>
            <Tooltip target=".export-buttons>button" position="bottom"/>

            <div className="card-header">
                <h2 className="card-title home-chart">All Agents Report</h2>
            </div>

            <div className="card-body">
                <div
                    style={{
                        marginBottom: '1.5rem',
                        padding: '1.5rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef',
                        width: '100%'
                    }}
                >
                    <div
                        className="flex"
                        style={{
                            gap: '1.5rem',
                            marginBottom: '1.5rem',
                            flexWrap: 'nowrap',
                            width: '100%'
                        }}
                    >
                        {/* From Date */}
                        <div style={{display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: '220px'}}>
                            <label htmlFor="fromDate" style={{fontWeight: 600, fontSize: '0.8rem'}}>
                                From Date
                            </label>
                            <Calendar
                                id="fromDate"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.value)}
                                dateFormat="yy-mm-dd"
                                showIcon
                                style={{width: '100%'}}
                            />
                        </div>

                        {/* To Date */}
                        <div style={{display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: '220px'}}>
                            <label htmlFor="toDate" style={{fontWeight: 600, fontSize: '0.8rem'}}>
                                To Date
                            </label>
                            <Calendar
                                id="toDate"
                                value={toDate}
                                onChange={(e) => setToDate(e.value)}
                                dateFormat="yy-mm-dd"
                                showIcon
                                style={{width: '100%'}}
                            />
                        </div>

                        {/* Agent ID */}
                        <div style={{display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: '220px'}}>
                            <label htmlFor="agentId" style={{fontWeight: 600, fontSize: '0.8rem'}}>
                                Agent ID
                            </label>
                            <InputText
                                id="agentId"
                                value={agentId}
                                onChange={(e) => {
                                    const numericValue = e.target.value.replace(/\D/g, '');
                                    setAgentId(numericValue);
                                }}
                                placeholder="Enter Agent ID"
                                inputMode="numeric"
                                style={{width: '100%'}}
                            />

                        </div>

                        {/* Search */}
                        <div style={{display: 'flex', flexDirection: 'column', gap: '0.4rem', flex: 1}}>
                            <label style={{fontWeight: 600, fontSize: '0.8rem'}}>
                                Search
                            </label>
                            <span className="p-input-icon-left">
                                <i className="pi pi-search"/>
                                {globalFilterElement}
                            </span>
                        </div>
                    </div>

                    <div className="flex justify-content-end gap-4">
                        <Button
                            label="Generate Report"
                            icon="pi pi-refresh"
                            className="m-1"
                            onClick={fetchAgentReports}
                            loading={loading}
                        />
                        <TieredMenu model={items} popup ref={menu} breakpoint="767px"/>
                        <Button
                            label="Download"
                            icon="pi pi-download"
                            className="m-1"
                            onClick={(e) => menu.current.toggle(e)}
                            disabled={payments.length === 0}
                            severity="secondary"
                        />
                    </div>
                </div>

                <DataTable
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    ref={dt}
                    value={payments}
                    loading={loading}
                    tableStyle={{minWidth: '50rem'}}
                    globalFilter={globalFilter}
                    exportFilename="agents-report"
                    emptyMessage="No payment records found. Please select a date range and generate report."
                >
                    <Column field="policyNumber" header="Policy Number" sortable/>
                    <Column field="productName" header="Product Name" sortable/>
                    <Column field="payerName" header="Payer Name" sortable/>
                    <Column field="mobileNumber" header="Mobile Number" sortable/>
                    <Column field="emailAddress" header="Email" sortable/>
                    <Column field="paymentMethod" header="Payment Method" sortable/>
                    <Column field="amount" header="Amount" body={amountBodyTemplate} sortable/>
                    <Column field="paymentStatus" header="Payment Status" body={paymentStatusBodyTemplate} sortable/>
                    <Column field="paymentReference" header="Payment Ref" sortable/>
                    <Column field="notes" header="Notes"/>
                    <Column
                        field="timeStamp"
                        header="Timestamp"
                        body={(rowData) => dateBodyTemplate(rowData, 'timeStamp')}
                        sortable
                    />
                </DataTable>
            </div>
        </div>
    );
};

export default AgentReportsTable;
